import { fetchData } from "../clients/api";
import { writeData } from "../clients/api";
import { Result } from "./types";
import { handleError } from "../errors";

// ─── Read operations ─────────────────────────────────────────────────────────

interface UseGroup {
  id: string;
  name: string;
}

// ─── Unit spec associations (for beds/baths/half_baths display) ─────────────

interface UnitSpec {
  id: string;
  name: string;
}

interface UnitSpecAssociation {
  id: string;
  unit_spec: string;
  use_group: string;
}

// ─── Write operations ────────────────────────────────────────────────────────

interface CreateProjectPayload {
  name: string;
  client_name: string;
  region: string;
  industry: string;
  city?: string;
  state?: string;
  public?: boolean;
  owner?: string;
}

interface CreateProjectResponse {
  data: { project: { id: string } };
}

interface CreateEstimatePayload {
  name: string;
  project: string;
  active?: boolean;
  primary?: boolean;
}

interface CreateFloorGroupPayload {
  estimate: string;
  label: string;
}

interface EstimateUseGroupEntry {
  use_group: string;
  quantity: number;
}

interface AddEstimateUseGroupsResponse {
  data: {
    estimate_use_groups: { id: string; order: number }[];
  };
}

interface CreateAreaMatrixItemPayload {
  estimate: string;
  floor_label: string;
  floor_number: number;
  floor_type: string;
  floor_group?: string;
  facade_perimeter?: number;
  facade_floor_to_floor?: number;
}

interface AreaAllocation {
  area_matrix_item: string;
  estimate_use_group: string;
  area: number;
  active_floor: boolean;
}

// ─── Unit type operations ───────────────────────────────────────────────────

interface CreateUnitTypePayload {
  estimate: string;
  label: string;
  estimate_use_group?: string;
  area?: number;
  beds?: number;
  baths?: number;
  half_baths?: number;
}

interface UpdateUnitTypePayload {
  area?: number;
  beds?: number;
  baths?: number;
  half_baths?: number;
  terminal_hvacs?: number;
  extras?: Record<string, unknown>;
}

// ─── Room operations ────────────────────────────────────────────────────────

interface CreateRoomPayload {
  estimate: string;
  label: string;
  estimate_use_group?: string;
  area_average?: number;
  quantity?: number;
  height?: number;
  perimeter?: number;
}

interface UpdateRoomPayload {
  area_average?: number;
  quantity?: number;
  height?: number;
  perimeter?: number;
}

export const fetchUseGroups = async (): Promise<Result<UseGroup[]>> => {
  try {
    const response = await fetchData({ table: "use_groups" });
    return { success: true, data: response?.data || [] };
  } catch (error) {
    return { success: false, error: handleError(error, { table: "use_groups" }) };
  }
};

export const fetchIndustries = async (): Promise<Result<{ label: string; value: string }[]>> => {
  try {
    const response = await fetchData({ table: "setup", params: { search: "key;projects:industries" } });
    const industries = response?.data?.[0]?.value || [];
    return { success: true, data: industries };
  } catch (error) {
    return { success: false, error: handleError(error, { table: "setup", key: "projects:industries" }) };
  }
};

export const fetchUnitSpecs = async (): Promise<Result<UnitSpec[]>> => {
  try {
    const response = await fetchData({ table: "unit_specs" });
    return { success: true, data: response?.data || [] };
  } catch (error) {
    return { success: false, error: handleError(error, { table: "unit_specs" }) };
  }
};

export const fetchUnitSpecAssociations = async (): Promise<Result<UnitSpecAssociation[]>> => {
  try {
    const response = await fetchData({ table: "unit_spec_associations" });
    return { success: true, data: response?.data || [] };
  } catch (error) {
    return { success: false, error: handleError(error, { table: "unit_spec_associations" }) };
  }
};

export const createUnitTypeUnitSpecAssociation = async (
  unitType: string,
  unitSpecAssociation: string,
  value: number
): Promise<Result<string>> => {
  try {
    const response = await writeData({
      table: "unit_type_unit_spec_associations",
      payload: { unit_type: unitType, unit_spec_association: unitSpecAssociation, value },
    });
    return { success: true, data: response.data.id };
  } catch (error) {
    return {
      success: false,
      error: handleError(error, { table: "unit_type_unit_spec_associations", operation: "create" }),
    };
  }
};

export const createProject = async (payload: CreateProjectPayload): Promise<Result<string>> => {
  try {
    const response: CreateProjectResponse = await writeData({
      table: "projects",
      payload: { ...payload, public: payload.public ?? false },
    });
    return { success: true, data: response.data.project.id };
  } catch (error) {
    return { success: false, error: handleError(error, { table: "projects", operation: "create" }) };
  }
};

export const createEstimate = async (payload: CreateEstimatePayload): Promise<Result<string>> => {
  try {
    const response = await writeData({
      table: "estimates",
      payload: { ...payload, active: payload.active ?? false, primary: payload.primary ?? false },
    });
    return { success: true, data: response.data.id };
  } catch (error) {
    return { success: false, error: handleError(error, { table: "estimates", operation: "create" }) };
  }
};

export const createFloorGroup = async (payload: CreateFloorGroupPayload): Promise<Result<string>> => {
  try {
    const response = await writeData({ table: "floor_groups", payload });
    return { success: true, data: response.data.id };
  } catch (error) {
    return { success: false, error: handleError(error, { table: "floor_groups", operation: "create" }) };
  }
};

export const createUseGroup = async (name: string, category: string = "GENERAL"): Promise<Result<string>> => {
  try {
    const response = await writeData({ table: "use_groups", payload: { name, category } });
    return { success: true, data: response.data.id };
  } catch (error) {
    return { success: false, error: handleError(error, { table: "use_groups", operation: "create" }) };
  }
};

export const addEstimateUseGroups = async (
  estimateId: string,
  entries: EstimateUseGroupEntry[]
): Promise<Result<{ id: string; order: number }[]>> => {
  try {
    const response: AddEstimateUseGroupsResponse = await writeData({
      table: "estimates",
      id: estimateId,
      subRoute: "use_groups",
      payload: { estimate_use_groups: entries },
      method: "PUT",
    });
    return { success: true, data: response.data.estimate_use_groups };
  } catch (error) {
    return {
      success: false,
      error: handleError(error, { table: "estimates", subRoute: "use_groups", operation: "update" }),
    };
  }
};

export const updateEstimateUseGroup = async (
  estimateId: string,
  updates: { id: string; label?: string; floor_group?: string; exclude_area?: boolean }[]
): Promise<Result<void>> => {
  try {
    await writeData({
      table: "estimates",
      id: estimateId,
      subRoute: "use_groups",
      payload: { estimate_use_groups: updates },
      method: "PUT",
    });
    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: handleError(error, { table: "estimates", subRoute: "use_groups", operation: "update" }),
    };
  }
};

export const createAreaMatrixItem = async (payload: CreateAreaMatrixItemPayload): Promise<Result<string>> => {
  try {
    const response = await writeData({ table: "area_matrix_items", payload });
    return { success: true, data: response.data.id };
  } catch (error) {
    return { success: false, error: handleError(error, { table: "area_matrix_items", operation: "create" }) };
  }
};

export const batchUpsertAreaAllocations = async (
  estimateId: string,
  amiId: string,
  allocations: AreaAllocation[]
): Promise<Result<void>> => {
  try {
    await writeData({
      table: "area_matrix_items",
      subRoute: "batch-upsert",
      payload: {
        estimate: estimateId,
        area_matrix_items: [
          {
            id: amiId,
            area_matrix_item_estimate_use_groups: allocations,
            area_matrix_item_estimate_transportations: [],
          },
        ],
      },
      method: "PUT",
    });
    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: handleError(error, { table: "area_matrix_items", subRoute: "batch-upsert", operation: "update" }),
    };
  }
};

export const createUnitType = async (payload: CreateUnitTypePayload): Promise<Result<string>> => {
  try {
    const response = await writeData({ table: "unit_types", payload });
    return { success: true, data: response.data.id };
  } catch (error) {
    return { success: false, error: handleError(error, { table: "unit_types", operation: "create" }) };
  }
};

export const updateUnitType = async (id: string, payload: UpdateUnitTypePayload): Promise<Result<void>> => {
  try {
    await writeData({ table: "unit_types", id, payload, method: "PUT" });
    return { success: true };
  } catch (error) {
    return { success: false, error: handleError(error, { table: "unit_types", operation: "update" }) };
  }
};

function wrapJsonbValue(value: number | undefined): { value: number } | undefined {
  return value !== undefined ? { value } : undefined;
}

export const createRoom = async (payload: CreateRoomPayload): Promise<Result<string>> => {
  try {
    const apiPayload: Record<string, unknown> = {
      estimate: payload.estimate,
      label: payload.label,
    };
    if (payload.estimate_use_group) apiPayload.estimate_use_group = payload.estimate_use_group;
    if (payload.height !== undefined) apiPayload.height = payload.height;

    const areaAvg = wrapJsonbValue(payload.area_average);
    if (areaAvg) apiPayload.area_average = areaAvg;
    const qty = wrapJsonbValue(payload.quantity);
    if (qty) apiPayload.quantity = qty;
    const perim = wrapJsonbValue(payload.perimeter);
    if (perim) apiPayload.perimeter = perim;

    const response = await writeData({ table: "rooms", payload: apiPayload });
    return { success: true, data: response.data.id };
  } catch (error) {
    return { success: false, error: handleError(error, { table: "rooms", operation: "create" }) };
  }
};

export const updateRoom = async (id: string, payload: UpdateRoomPayload): Promise<Result<void>> => {
  try {
    const apiPayload: Record<string, unknown> = {};
    if (payload.height !== undefined) apiPayload.height = payload.height;

    const areaAvg = wrapJsonbValue(payload.area_average);
    if (areaAvg) apiPayload.area_average = areaAvg;
    const qty = wrapJsonbValue(payload.quantity);
    if (qty) apiPayload.quantity = qty;
    const perim = wrapJsonbValue(payload.perimeter);
    if (perim) apiPayload.perimeter = perim;

    await writeData({ table: "rooms", id, payload: apiPayload, method: "PUT" });
    return { success: true };
  } catch (error) {
    return { success: false, error: handleError(error, { table: "rooms", operation: "update" }) };
  }
};
